import { normaliseName, type Suggestions, type WineRating } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { deleteWineRating, saveWineRating } from "../api.js";
import { useMayWrite } from "../cellar.js";
import { formatScore } from "../score.js";
import { actionButton, control, fieldLabel, resting } from "./controls.js";
import { Picker } from "./Picker.js";

/**
 * What other people think of this wine.
 *
 * The second of the three kinds of score, and the one that was written and
 * never read: `WineRating` has been in the schema since `2.6`, the import wrote
 * 119 of them off the spreadsheet's Vivino column, and until this component
 * existed not one of them could be seen. A cellar that records somebody else's
 * judgement and then cannot show it is keeping a column nobody can read.
 *
 * **Every reading carries its scale and its date, and neither is decoration.**
 * 4.1 and 17 and 92 are all respectable numbers and none of them means anything
 * without the number it is out of; and a Vivino average moves as more people
 * drink the wine, so *4.1 in 2026* stays true after the average has moved on,
 * where a bare 4.1 quietly becomes a claim about today.
 *
 * The source is typed through the same picker the producer uses, against the
 * cellar's own list. A cellar's vocabulary is its own — Vivino, Munskänkarna, a
 * magazine, the friend whose palate you trust — and a fixed list here would
 * have to be extended by a deployment every time one of them changed.
 */
export function WineRatings({
  cellarId,
  wineId,
  ratings,
  suggestions,
  onChanged,
}: {
  cellarId: string;
  wineId: string;
  ratings: readonly WineRating[];
  suggestions: Suggestions | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WineRating | null>(null);
  const mayWrite = useMayWrite();

  const start = (rating: WineRating | null) => {
    setEditing(rating);
    setOpen(true);
  };

  return (
    <div className="mt-3">
      {ratings.length === 0 ? (
        // *Än ditt eget* is the owner's sentence and says nothing true to a
        // guest, who has no rating here and cannot leave one.
        <p className="text-muted max-w-prose text-sm">
          {t(mayWrite === false ? "wine.ratings.noneForGuest" : "wine.ratings.none")}
        </p>
      ) : (
        <ul>
          {ratings.map((rating) => (
            <li
              key={rating.id}
              className="border-line flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b py-3"
            >
              <span className="flex items-baseline gap-3">
                <span>{rating.sourceName}</span>
                <span className="font-display text-lg">
                  {formatScore(rating.score, rating.scaleHasDecimals)}
                  <span className="text-muted text-sm"> / {rating.scaleMax}</span>
                </span>
              </span>
              <span className="text-muted flex items-baseline gap-4 text-sm">
                {rating.ratingCount !== null && (
                  <span>{t("wine.ratings.count", { count: rating.ratingCount })}</span>
                )}
                <span>{rating.readOn}</span>
                {/* Somebody else's judgement of the wine is not price data and
                    a guest reads it; recording and correcting it is the
                    cellar's own act. */}
                {mayWrite === true && (
                  <>
                    <button
                      type="button"
                      className="text-accent underline-offset-4 hover:underline"
                      onClick={() => start(rating)}
                    >
                      {t("wine.ratings.edit")}
                    </button>
                    <button
                      type="button"
                      className="hover:text-past underline-offset-4 hover:underline"
                      onClick={() => {
                        void deleteWineRating(cellarId, wineId, rating.id).then(
                          onChanged,
                        );
                      }}
                    >
                      {t("wine.ratings.remove")}
                    </button>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {open ? (
        <RatingForm
          cellarId={cellarId}
          wineId={wineId}
          suggestions={suggestions}
          editing={editing}
          onDone={() => {
            setOpen(false);
            setEditing(null);
            onChanged();
          }}
          onCancel={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      ) : (
        mayWrite === true && (
          <button
            type="button"
            className="text-accent mt-3 text-sm underline-offset-4 hover:underline"
            onClick={() => start(null)}
          >
            {t("wine.ratings.add")}
          </button>
        )
      )}
    </div>
  );
}

function RatingForm({
  cellarId,
  wineId,
  suggestions,
  editing,
  onDone,
  onCancel,
}: {
  cellarId: string;
  wineId: string;
  suggestions: Suggestions | null;
  /** The reading being corrected, or null for a new one. */
  editing: WineRating | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const sources = suggestions?.ratingSources ?? [];

  const [sourceName, setSourceName] = useState(editing?.sourceName ?? "");
  const [score, setScore] = useState(editing === null ? "" : String(editing.score));
  const [readOn, setReadOn] = useState(
    editing?.readOn ?? new Date().toISOString().slice(0, 10),
  );
  const [ratingCount, setRatingCount] = useState(
    editing?.ratingCount === null || editing === null ? "" : String(editing.ratingCount),
  );
  const [scaleMax, setScaleMax] = useState("100");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A source the cellar already uses knows what its numbers are out of, and the
  // scale is shown rather than asked for. Matched on the folded name, like
  // every other reference row, so `vivino` finds `Vivino`.
  const known = sources.find(
    (source) => normaliseName(source.name) === normaliseName(sourceName),
  );
  const max = known?.scaleMax ?? Number(scaleMax);
  const value = Number(score.replace(",", "."));
  const sound =
    score.trim() !== "" &&
    Number.isFinite(value) &&
    value >= 0 &&
    Number.isFinite(max) &&
    max > 0 &&
    value <= max;
  const canSubmit = sourceName.trim() !== "" && sound && !busy;

  return (
    <form
      className="border-line mt-4 grid gap-4 border-t pt-5 sm:grid-cols-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        void saveWineRating(cellarId, wineId, {
          sourceName: sourceName.trim(),
          // Sent only for a source that does not exist yet. An existing one
          // already knows its scale, and letting this restate it would let one
          // reading change what every earlier reading from that source meant.
          scaleMax: known === undefined ? Number(scaleMax) : null,
          scaleHasDecimals: known?.scaleHasDecimals ?? true,
          score: value,
          readOn,
          ratingCount: ratingCount.trim() === "" ? null : Number(ratingCount),
          url: null,
          note: null,
        })
          .then(onDone)
          .catch(() => setError(t("wine.ratings.saveFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div className="sm:col-span-2">
        <Picker
          id="rating-source"
          label={t("wine.ratings.source")}
          value={sourceName}
          onChange={setSourceName}
          options={sources.map((source) => source.name)}
          placeholder={t("wine.ratings.sourceHint")}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor="rating-score">
          {known === undefined
            ? t("wine.ratings.score")
            : t("wine.ratings.scoreOf", { max: known.scaleMax })}
        </label>
        <input
          id="rating-score"
          inputMode="decimal"
          value={score}
          onChange={(event) => setScore(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      {/* Only for a source nobody has used before. Guessing a hundred would
          file Munskänkarna's 17 as a third of a score. */}
      {known === undefined && (
        <div>
          <label className={fieldLabel} htmlFor="rating-scale">
            {t("wine.ratings.scale")}
          </label>
          <input
            id="rating-scale"
            inputMode="numeric"
            value={scaleMax}
            onChange={(event) => setScaleMax(event.target.value)}
            className={`${control} ${resting} mt-1`}
          />
        </div>
      )}

      <div>
        <label className={fieldLabel} htmlFor="rating-date">
          {t("wine.ratings.readOn")}
        </label>
        <input
          id="rating-date"
          type="date"
          value={readOn}
          onChange={(event) => setReadOn(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor="rating-count">
          {t("wine.ratings.ratingCount")}
        </label>
        <input
          id="rating-count"
          inputMode="numeric"
          value={ratingCount}
          onChange={(event) => setRatingCount(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div className="col-span-full flex items-center gap-4">
        <button type="submit" disabled={!canSubmit} className={actionButton}>
          {busy ? t("wine.saving") : t("wine.ratings.save")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
        >
          {t("wine.cancel")}
        </button>
        {error !== null && (
          <span role="alert" className="text-past text-sm">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}
